import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CREDIT_API_URL } from '../Constants.js';

const OtpVerificationModal = ({
  mobile,
  productType,
  onClose,
  onVerified,
  show,
  onError
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(300);
  const [resendTimer, setResendTimer] = useState(30);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Reset OTP fields when modal appears
  useEffect(() => {
    if (show) {
      setOtp(['', '', '', '', '', '']);
      setOtpError('');
      setOtpSuccess('');
      setTimer(300);
      setResendTimer(30);
      setVerifying(false);
      setIsResending(false);
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  useEffect(() => {
    let interval;
    if (show && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [show, timer]);

  useEffect(() => {
    let interval;
    if (show && resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [show, resendTimer]);

  useEffect(() => {
    if (show && timer === 0) {
      setOtpError("OTP has expired. Please resend.");
    }
  }, [timer, show]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (show) {
      const firstInput = document.querySelector('.otp-input');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }
  }, [show]);

  // Add global paste handler to modal
  useEffect(() => {
    if (show) {
      const modalElement = document.querySelector('.otp-modal');
      if (modalElement) {
        const handleGlobalPaste = (e) => {
          // Only handle paste if no input is focused or if it's not an OTP input
          const activeElement = document.activeElement;
          if (!activeElement || !activeElement.classList.contains('otp-input')) {
            handlePaste(e);
          }
        };
        
        modalElement.addEventListener('paste', handleGlobalPaste);
        
        return () => {
          modalElement.removeEventListener('paste', handleGlobalPaste);
        };
      }
    }
  }, [show]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 400); // Wait for fade out animation
  };

  const handleOtpChange = (e, idx) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    
    // Auto-focus to next input if a value was entered and we're not at the last input
    if (value && idx < 5) {
      const nextInput = document.querySelectorAll('.otp-input')[idx + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleInputClick = (e, idx) => {
    // Just select the text, don't clear on click
    e.target.select();
  };

  const handleKeyDown = (e, idx) => {
    // Handle Enter key to verify OTP when all fields are filled
    if (e.key === 'Enter') {
      const code = otp.join('');
      if (code.length === 6) {
        handleVerifyOtp();
      }
      return;
    }

    // Handle backspace key
    if (e.key === 'Backspace') {
      if (otp[idx] === '') {
        // If current input is empty, move to previous input
        if (idx > 0) {
          const prevInput = document.querySelectorAll('.otp-input')[idx - 1];
          if (prevInput) {
            prevInput.focus();
            // Clear the previous input
            const newOtp = [...otp];
            newOtp[idx - 1] = '';
            setOtp(newOtp);
          }
        }
      } else {
        // If current input has value, clear it
        const newOtp = [...otp];
        newOtp[idx] = '';
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    // Extract only numeric characters from pasted data
    const numericOnly = pastedData.replace(/[^0-9]/g, '');
    
    // Only process if we have numeric data
    if (numericOnly.length > 0) {
      const newOtp = [...otp];
      
      // Fill the OTP array with pasted digits (up to 6 digits)
      for (let i = 0; i < Math.min(numericOnly.length, 6); i++) {
        newOtp[i] = numericOnly[i];
      }
      
      // Clear any remaining fields if pasted data is shorter than 6 digits
      for (let i = numericOnly.length; i < 6; i++) {
        newOtp[i] = '';
      }
      
      setOtp(newOtp);
      
      // Focus the appropriate input after pasting
      const targetIndex = Math.min(numericOnly.length, 5);
      const inputs = document.querySelectorAll('.otp-input');
      if (inputs[targetIndex]) {
        setTimeout(() => inputs[targetIndex].focus(), 0);
      }
      
      // Clear any previous errors
      setOtpError('');
      setOtpSuccess(''); // Clear success message on new paste
      
      // If we have a complete 6-digit OTP, show a success message
      if (numericOnly.length === 6) {
        setOtpSuccess('OTP pasted successfully! Click "Verify Code" to proceed.');
        setTimeout(() => setOtpSuccess(''), 2000); // Clear the message after 2 seconds
      }
    }
  };

  const handleCopy = (e) => {
    // Allow copy functionality
    e.stopPropagation();
  };

  const handleCut = (e) => {
    // Allow cut functionality
    e.stopPropagation();
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) {
      setOtpError(`Please wait ${resendTimer} seconds before requesting another OTP.`);
      return;
    }

    setIsResending(true);
    try {
      const response = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
        mobile: parseInt(mobile),
        productType: productType
      });
      if (response.status === 200) {
        setTimer(300);
        setResendTimer(30);
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
        setOtpSuccess(''); // Clear success message on resend
      } else {
        setOtpError('Failed to resend OTP.');
      }
    } catch (err) {
      setOtpError('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setVerifying(true);
    setOtpError('');
    setOtpSuccess(''); // Clear success message on verification attempt
    const code = otp.join('');
    if (code.length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      setVerifying(false);
      return;
    }

    try {
      const response = await axios.post(`${CREDIT_API_URL}/verify-otp`, {
        mobile: parseInt(mobile),
        productType: productType,
        otp: code
      });

      if (response.status === 200) {
        onVerified(); // success callback
        handleClose();
      } else {
        setOtpError(response.data.message || "Invalid OTP.");
      }
    } catch (error) {
      setOtpError(error.response?.data?.message || "Invalid OTP.");
    } finally {
      setVerifying(false);
    }
  };

  if (!show) return null;

  return (
    <div className={`otp-modal-overlay ${isVisible ? 'show' : ''}`}>
      <div className={`otp-modal ${isVisible ? 'show' : ''}`}>
        <button className="otp-close-btn" onClick={handleClose}>✕</button>
        <div className="otp-modal-content">
          <p>We've sent a 6-digit OTP to your mobile number
            <b> +91 XXXXX XXX{String(mobile).slice(-2)}</b>
          </p>

          <div className="otp-inputs">
            {otp.map((digit, idx) => (
              <input
                key={idx}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(e, idx)}
                onClick={e => handleInputClick(e, idx)}
                onKeyDown={e => handleKeyDown(e, idx)}
                onPaste={handlePaste}
                onCopy={handleCopy}
                onCut={handleCut}
                className="otp-input"
                placeholder=""
              />
            ))}
          </div>

          

          <button className="otp-verify-btn" onClick={handleVerifyOtp} disabled={verifying || otp.join('').length !== 6}>
            {verifying ? 'Verifying...' : 'Verify Code'}
          </button>
          {otpError && <div className="otp-error">{otpError}</div>}
          <div className="otp-meta">
            <span>
              Code expires in: <span style={{ color: '#F85A5A' }}>
                {`${Math.floor(timer / 60).toString().padStart(2, '0')}:${(timer % 60).toString().padStart(2, '0')}`}
              </span>
            </span>
            <span style={{ marginLeft: 16 }}>
              Didn't receive code?
              <button className="otp-resend" onClick={handleResendOtp} disabled={isResending || resendTimer > 0}>
                {resendTimer > 0 ? `Wait ${resendTimer}s` : 'Resend'}
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerificationModal;
